import { Head, Link, router, usePage } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import DataTable, { Column } from '@/components/DataTable'
import AgentPasswordActions from '@/components/AgentPasswordActions'
import { PageProps } from '@/types'
import { UserPlus, Pencil, Power, PowerOff } from 'lucide-react'

import { ManagedUser, userPasswordEndpoints } from './shared'

interface Props extends PageProps {
  users: ManagedUser[]
  current_user_id: number
  can_manage_owners: boolean
  sendgrid_usable: boolean
}

const ROLE_STYLES: Record<string, string> = {
  owner: 'bg-chestnut-forest/10 text-chestnut-forest',
  administrator: 'bg-chestnut-forest/10 text-chestnut-forest',
  staff: 'bg-chestnut-charcoal/10 text-chestnut-charcoal',
  agent: 'bg-chestnut-charcoal/5 text-chestnut-charcoal/70',
}

export default function AdminUsersIndex({ users, can_manage_owners, sendgrid_usable }: Props) {
  const flash = usePage<PageProps>().props.flash
  const initialPassword = flash?.user_initial_password

  function toggleActive(u: ManagedUser) {
    if (u.is_active) {
      const ok = confirm(
        `Switch off ${u.full_name}?\n\nThey are signed out immediately and cannot sign back in until the account is switched on again.`
      )
      if (!ok) return
      router.post(`/admin/users/${u.id}/deactivate`, {}, { preserveScroll: true })
    } else {
      router.post(`/admin/users/${u.id}/reactivate`, {}, { preserveScroll: true })
    }
  }

  // An owner's account is owner-only territory, matching the server guard, so
  // an administrator is not shown controls the server would refuse.
  function canManage(u: ManagedUser): boolean {
    return u.role !== 'owner' || can_manage_owners
  }

  const columns: Column<ManagedUser>[] = [
    {
      key: 'full_name',
      header: 'Name',
      sortable: true,
      render: (u) => (
        <div>
          <span className="font-medium text-chestnut-charcoal">{u.full_name}</span>
          {u.is_self && <span className="ml-2 text-xs text-chestnut-charcoal/50">(you)</span>}
        </div>
      ),
    },
    { key: 'email', header: 'Email', sortable: true },
    {
      key: 'role_label',
      header: 'Role',
      sortable: true,
      render: (u) => (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
            ROLE_STYLES[u.role] ?? ROLE_STYLES.agent
          }`}
        >
          {u.role_label}
        </span>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      sortable: true,
      render: (u) =>
        u.is_active ? (
          <span className="text-sm text-status-success">Active</span>
        ) : (
          <span className="text-sm text-status-error">Switched off</span>
        ),
    },
    {
      key: 'has_agent_record',
      header: 'Agent record',
      sortable: true,
      render: (u) =>
        u.has_agent_record ? (
          <Link
            href={`/agents/${u.agent_id}`}
            className="text-sm text-chestnut-forest hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            In agent tree
          </Link>
        ) : (
          <span className="text-sm text-chestnut-charcoal/50">Not an agent</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (u) =>
        canManage(u) ? (
          <div className="flex items-center justify-end gap-3" onClick={(e) => e.stopPropagation()}>
            <Link
              href={`/admin/users/${u.id}/edit`}
              className="inline-flex items-center gap-1 text-sm text-chestnut-forest hover:underline"
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Link>
            {/* Password actions are hidden for your own row: resending a
                welcome email mints a new password, which would destroy the
                credentials of the session you are using. Profile handles it. */}
            {!u.is_self && (
              <AgentPasswordActions
                agentId={u.id}
                agentName={u.full_name}
                sendgridConnected={sendgrid_usable}
                endpoints={userPasswordEndpoints(u.id)}
                passwordParamKey="user_password"
              />
            )}
            {!u.is_self && (
              <button
                onClick={() => toggleActive(u)}
                className={`inline-flex items-center gap-1 text-sm font-semibold ${
                  u.is_active ? 'text-status-error hover:underline' : 'text-status-success hover:underline'
                }`}
              >
                {u.is_active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                {u.is_active ? 'Switch off' : 'Switch on'}
              </button>
            )}
          </div>
        ) : (
          <span className="text-xs text-chestnut-charcoal/40">Owner — managed by an owner</span>
        ),
    },
  ]

  return (
    <AppLayout>
      <Head title="Users" />
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading">Users</h1>
            <p className="text-sm text-chestnut-charcoal/60 mt-1 max-w-2xl">
              Every account in your organization. Staff, administrator and owner accounts created here have no
              agent record and never appear in the hierarchy tree. Agents are created on the Agents page so
              their place in the tree is created with them.
            </p>
          </div>
          <Link href="/admin/users/new" className="btn-primary inline-flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Add user
          </Link>
        </div>

        {flash?.notice && (
          <div className="rounded border border-status-success/40 bg-status-success/10 px-4 py-3 text-sm text-status-success">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="rounded border border-status-error/40 bg-status-error/10 px-4 py-3 text-sm text-status-error">
            {flash.alert}
          </div>
        )}

        {initialPassword && (
          <div className="rounded border border-chestnut-forest/40 bg-chestnut-forest/5 px-4 py-3">
            <p className="text-sm font-semibold text-chestnut-charcoal">
              Temporary password for {initialPassword.name}
            </p>
            <p className="text-sm text-chestnut-charcoal/70 mt-1">
              This is shown once. Send it to {initialPassword.email} and ask them to change it after signing in.
            </p>
            <code className="mt-2 inline-block rounded bg-white px-3 py-1.5 font-mono text-sm text-chestnut-charcoal">
              {initialPassword.password}
            </code>
          </div>
        )}

        <div className="card">
          <DataTable
            data={users}
            columns={columns}
            getRowKey={(u) => u.id}
            searchKeys={['full_name', 'email', 'role_label']}
            searchPlaceholder="Search users..."
            emptyMessage="No users found."
            pageSize={15}
          />
        </div>
      </div>
    </AppLayout>
  )
}
